import { Component, input, output, signal, OnInit, ChangeDetectionStrategy } from "@angular/core";

import { Workflow } from "../workflow/service/Workflow";
import { UserprefService } from "../../services/userpref.service";
import { IngestionFormComponent } from "./ingestion.form.component";
import { S25Util } from "../../util/s25-util";
import { S25IconComponent } from "../s25-icon/s25.icon.component";

@Component({
    selector: "s25-ng-linked-forms",
    template: `
        @if (items().length > 0) {
            <div class="linked-forms-section" [class.linked-forms-section--action-needed]="title() === 'Action Needed'">
                <h4 class="linked-forms-title" [class.linked-forms-title--action-needed]="title() === 'Action Needed'">
                    @if (title() === "Action Needed") {
                        <s25-ng-icon [type]="'alertTriangle'" [size]="16" [ariaLabel]="false" />
                    }
                    {{ title() }}
                </h4>
                <div class="linked-forms-list">
                    @for (item of items(); track getItemKey(item)) {
                        <div class="linked-form-item" [class.expanded]="expandedItems().has(getItemKey(item))">
                            <div class="linked-form-header" (click)="toggleItem(item)">
                                <div class="linked-form-info">
                                    <span class="linked-form-name">{{ item.name || "Form" }}</span>
                                    @if (item.contactName) {
                                        <span class="linked-form-contact">
                                            Assigned to: {{ item.contactName }}
                                            @if (item.contactEmail) {
                                                ({{ item.contactEmail }})
                                            }
                                        </span>
                                    }
                                    <span
                                        class="linked-form-status"
                                        [class]="'status-' + (item.workflowStatus?.toLowerCase() || '')"
                                    >
                                        {{ item.workflowStatus || "Pending" }}
                                    </span>
                                </div>
                                <div class="linked-form-actions">
                                    @if (hasChildren(item)) {
                                        <span class="expand-icon" [class.expand-up]="mode() === 'parents'">
                                            @if (mode() === "children") {
                                                {{ expandedItems().has(getItemKey(item)) ? "▼" : "▶" }}
                                            } @else {
                                                {{ expandedItems().has(getItemKey(item)) ? "▲" : "◀" }}
                                            }
                                        </span>
                                    }
                                </div>
                            </div>
                            @if (expandedItems().has(getItemKey(item))) {
                                <div class="linked-form-details">
                                    @if (mode() === "parents" && hasChildren(item)) {
                                        <s25-ng-linked-forms
                                            [items]="getChildren(item)"
                                            [title]="'Parent Forms'"
                                            [mode]="mode()"
                                            (onContinueForm)="onContinueForm.emit($event)"
                                        ></s25-ng-linked-forms>
                                    }
                                    <div class="linked-form-meta">
                                        <span>ID: {{ item.itemId }}</span>
                                        @if (item.currentWorkflowStage?.stageName) {
                                            <span>Stage: {{ item.currentWorkflowStage.stageName }}</span>
                                        }
                                    </div>
                                    @if (canPreviewForm(item)) {
                                        <div class="linked-form-actions-section">
                                            @if (!item.submitted && isOwnForm(item)) {
                                                <button
                                                    class="continue-button"
                                                    (click)="continueForm(item)"
                                                    title="Continue this form"
                                                >
                                                    Continue Form
                                                </button>
                                            }
                                            <div class="linked-form-preview">
                                                @if (previewedSubmissions().has(getItemKey(item))) {
                                                    <div class="preview-form-container">
                                                        <s25-ng-ingestion-form
                                                            [submissionIdUuid]="item.submissionIdUuid"
                                                            [ingestionPointId]="getIngestionPointId(item)"
                                                            [readOnly]="true"
                                                        ></s25-ng-ingestion-form>
                                                    </div>
                                                } @else {
                                                    <button class="preview-button" (click)="loadFormPreview(item)">
                                                        Preview Form
                                                    </button>
                                                }
                                            </div>
                                        </div>
                                    }
                                    @if (mode() === "children" && hasChildren(item)) {
                                        <s25-ng-linked-forms
                                            [items]="getChildren(item)"
                                            [title]="'Action Needed'"
                                            [mode]="mode()"
                                            (onContinueForm)="onContinueForm.emit($event)"
                                        ></s25-ng-linked-forms>
                                    }
                                </div>
                            }
                        </div>
                    }
                </div>
            </div>
        }
    `,
    styles: `
        .linked-forms-section {
            margin-top: 16px;
            padding: 12px;
            background: #f8f9fa;
            border-radius: 8px;
            border: 1px solid #dee2e6;
        }

        .linked-forms-title {
            display: flex;
            align-items: center;
            gap: 8px;
            color: var(--color--text);
        }

        .linked-forms-title--action-needed {
            color: var(--color--text-danger);
        }

        .linked-forms-title--action-needed ::ng-deep s25-ng-icon svg {
            stroke: var(--color--text-danger);
            fill: none;
        }

        .linked-forms-section--action-needed {
            border-color: color-mix(in srgb, var(--color--danger) 35%, var(--color--border));
        }

        .linked-forms-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        .linked-form-item {
            background: white;
            border: 1px solid #dee2e6;
            border-radius: 4px;
            overflow: hidden;
        }

        .linked-form-header {
            padding: 12px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            cursor: pointer;
            transition: background-color 0.2s;
        }

        .linked-form-header:hover {
            background-color: #f8f9fa;
        }

        .linked-form-info {
            display: flex;
            flex-direction: column;
            gap: 4px;
            flex: 1;
        }

        .linked-form-name {
            font-weight: 500;
            color: #333;
        }

        .linked-form-contact {
            font-size: 0.8rem;
            color: #555;
        }

        .linked-form-status {
            font-size: 0.75rem;
            padding: 2px 6px;
            border-radius: 4px;
            font-weight: bold;
            text-transform: uppercase;
            align-self: flex-start;
            color: #333;
        }

        .status-pending,
        .status-in_progress {
            background-color: #d1ecf1;
            color: #0c5460;
        }

        .status-completed {
            background-color: #d4edda;
            color: #155724;
        }

        .status-denied {
            background-color: #f8d7da;
            color: #721c24;
        }

        .status-requested_changes {
            background-color: #ffeaa7;
            color: #856404;
        }

        .linked-form-actions {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .expand-icon {
            color: #666;
            font-size: 0.75rem;
        }

        .expand-icon.expand-up {
            color: #6c757d;
        }

        .linked-form-details {
            padding: 12px;
            border-top: 1px solid #dee2e6;
            background: #f8f9fa;
        }

        .linked-form-meta {
            display: flex;
            gap: 16px;
            font-size: 0.875rem;
            color: #666;
            margin-bottom: 12px;
        }

        .linked-form-actions-section {
            margin-top: 12px;
            margin-bottom: 12px;
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        .linked-form-preview {
            margin-top: 0;
            margin-bottom: 0;
        }

        .preview-loading {
            padding: 20px;
            text-align: center;
            color: #666;
            font-style: italic;
        }

        .preview-button {
            padding: 8px 16px;
            background-color: #007bff;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 0.875rem;
            transition: background-color 0.2s;
        }

        .preview-button:hover {
            background-color: #0056b3;
        }

        .continue-button {
            padding: 8px 16px;
            background-color: #28a745;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 0.875rem;
            font-weight: 500;
            transition: background-color 0.2s;
        }

        .continue-button:hover {
            background-color: #218838;
        }

        .preview-form-container {
            border: 1px solid #dee2e6;
            border-radius: 4px;
            background: white;
            padding: 12px;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IngestionFormComponent, S25IconComponent],
})
export class LinkedFormsComponent implements OnInit {
    items = input<Workflow.WorkflowItem[]>([]);
    title = input<string>("Linked Forms");
    mode = input<"children" | "parents">("children"); // "children" for My Forms, "parents" for My Workflow
    onContinueForm = output<{ submissionIdUuid: string; ingestionPointId: number; itemId: number; contId?: number }>();

    expandedItems = signal<Set<string>>(new Set());
    previewedSubmissions = signal<Set<string>>(new Set());
    dateFormat = signal<string>("");
    currentContId = signal<number | undefined>(undefined);

    async ngOnInit() {
        const [dateFormat, contId] = await Promise.all([
            UserprefService.getS25DateTimeformat(),
            UserprefService.getContactId(),
        ]);
        this.dateFormat.set(dateFormat);
        this.currentContId.set(contId);
    }

    getItemKey(item: Workflow.WorkflowItem): string {
        return `${item.itemTypeId}:${item.itemId}`;
    }

    hasChildren(item: Workflow.WorkflowItem): boolean {
        if (this.mode() === "children") {
            return (item.childrenItems?.length || 0) > 0;
        } else {
            return item.parent != null;
        }
    }

    getChildren(item: Workflow.WorkflowItem): Workflow.WorkflowItem[] {
        if (this.mode() === "children") {
            return item.childrenItems || [];
        } else {
            // For parents mode, return the parent and its ancestors as a chain
            const parentChain: Workflow.WorkflowItem[] = [];
            let current: Workflow.WorkflowItem | undefined = item.parent;
            while (current) {
                parentChain.push(current);
                current = current.parent;
            }
            return parentChain;
        }
    }

    toggleItem(item: Workflow.WorkflowItem) {
        const key = this.getItemKey(item);
        const expanded = new Set(this.expandedItems());
        if (expanded.has(key)) {
            expanded.delete(key);
            // Also remove from previewed when collapsing
            const previewed = new Set(this.previewedSubmissions());
            previewed.delete(key);
            this.previewedSubmissions.set(previewed);
        } else {
            expanded.add(key);
            this.loadFormPreview(item); // Auto-load preview when expanding
        }
        this.expandedItems.set(expanded);
    }

    canPreviewForm(item: Workflow.WorkflowItem): boolean {
        // Can preview if it's an ingestion submission
        return item.itemTypeId === Workflow.WORKFLOW_INGESTION_SUBMISSION_OBJECT_TYPE;
    }

    getIngestionPointId(item: Workflow.WorkflowItem): number | null {
        if (item.itemTypeId === Workflow.WORKFLOW_INGESTION_SUBMISSION_OBJECT_TYPE) {
            // For ingestion submissions, parentItemId is the ingestionPointId
            return item.parentItemId || null;
        }
        return null;
    }

    async loadFormPreview(item: Workflow.WorkflowItem) {
        if (!this.canPreviewForm(item)) {
            return;
        }
        if (this.previewedSubmissions().has(this.getItemKey(item))) {
            return;
        }
        const previewed = new Set(this.previewedSubmissions());
        previewed.add(this.getItemKey(item));
        this.previewedSubmissions.set(previewed);
    }

    isOwnForm(item: Workflow.WorkflowItem): boolean {
        const contId = this.currentContId();
        return contId != null && item.contId === contId;
    }

    continueForm(item: Workflow.WorkflowItem) {
        const ingestionPointId = this.getIngestionPointId(item);

        if (!item.submissionIdUuid || !ingestionPointId || !item.itemId) {
            S25Util.showError("Missing submission UUID, ingestionPointId, or itemId for linked form");
            return;
        }

        this.onContinueForm.emit({
            submissionIdUuid: item.submissionIdUuid,
            ingestionPointId: ingestionPointId,
            itemId: item.itemId,
            contId: item.contId,
        });
    }
}
